import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth.js";
import { FamilyProvider, useFamily } from "./lib/family.js";
import { Landing } from "./pages/Landing.js";
import { AuthCallback } from "./pages/AuthCallback.js";
import { Onboarding } from "./pages/Onboarding.js";
import { CalendarPage } from "./pages/Calendar.js";
import { MembersPage } from "./pages/Members.js";
import { SettingsPage } from "./pages/Settings.js";
import { SmsConsent } from "./pages/SmsConsent.js";
import { HowItWorks } from "./pages/HowItWorks.js";
import { DevBanner } from "./components/DevBanner.js";
import { FeedbackButton } from "./components/FeedbackButton.js";

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FamilyProvider>
          {/* DevBanner sits above every route — auth, marketing, and signed-in
              app pages all see the same "active development" framing. */}
          <DevBanner />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/sms-consent" element={<SmsConsent />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/calendar" element={<Protected requiresFamily><CalendarPage /></Protected>} />
            <Route path="/members" element={<Protected requiresFamily><MembersPage /></Protected>} />
            <Route path="/settings" element={<Protected requiresFamily><SettingsPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {/* Feedback widget — floating button on every page, including the
              landing variants. The modal collects {interactionPreference,
              frictionPoint, body, email}. */}
          <FeedbackButton />
        </FamilyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function RootRedirect() {
  const { session, loading: authLoading } = useAuth();
  const { family, loading: familyLoading } = useFamily();
  if (authLoading) return null;
  if (!session) return <Landing />;
  if (familyLoading) return null;
  return <Navigate to={family ? "/calendar" : "/onboarding"} replace />;
}

function Protected({
  children,
  requiresFamily,
}: {
  children: React.ReactNode;
  requiresFamily?: boolean;
}) {
  const { session, loading: authLoading } = useAuth();
  const { family, loading: familyLoading } = useFamily();
  if (authLoading || (requiresFamily && familyLoading)) return null;
  if (!session) return <Navigate to="/" replace />;
  if (requiresFamily && !family) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}
